import { initializeApp } from "firebase/app";
import {
  getDatabase,
  onChildAdded,
  orderByChild,
  query,
  ref,
  startAt,
} from "firebase/database";

import { API, ErrorApi } from "./client";

/**
 * Subir una hoja de vida y esperar a que el backend la lea.
 *
 * El analisis NO es sincrono: Directus responde al subir el archivo, y solo
 * despues un hook lo procesa con Bedrock, crea el CV y publica el resultado en
 * Firebase Realtime. Por eso hacen falta dos pasos y no basta con un POST.
 */

const firebaseConfig = {
  apiKey: "AIzaSyBpSs9K8zzNZt3ukbhtIDcyUwDBgStHDb0",
  authDomain: "almia-3964f.firebaseapp.com",
  databaseURL: "https://almia-3964f-default-rtdb.firebaseio.com/",
  projectId: "almia-3964f",
  storageBucket: "almia-3964f.firebasestorage.app",
  messagingSenderId: "1073100329659",
  appId: "1:1073100329659:web:c4ca5fdb306ecb5aafcf9e",
};

export const MAX_BYTES = 5 * 1024 * 1024;
export const EXTENSIONES = [".pdf", ".docx"];

/** Techo de espera. Pasado esto se ofrece seguir a mano en vez de dejar girando. */
const ESPERA_MAX_MS = 90_000;

export const validarArchivo = (archivo: File): string | null => {
  const ext = `.${archivo.name.split(".").pop()?.toLowerCase() ?? ""}`;
  if (!EXTENSIONES.includes(ext)) {
    return "Solo aceptamos PDF o Word (.docx).";
  }
  if (archivo.size > MAX_BYTES) {
    return "El archivo pesa más de 5 MB. Prueba a exportarlo de nuevo con menos imágenes.";
  }
  return null;
};

/** Sube el archivo a Directus. Devuelve el `session_id` que emparejara el evento. */
export const subirArchivo = async (
  archivo: File,
  onProgreso?: (porcentaje: number) => void,
): Promise<{ sessionId: string; fileId: string }> =>
  new Promise((resolver, rechazar) => {
    const datos = new FormData();
    datos.append("file", archivo);

    // XMLHttpRequest y no fetch: es la unica forma de tener progreso de subida,
    // y en un movil con 5 MB esa barra es la diferencia entre esperar y cerrar.
    const peticion = new XMLHttpRequest();
    peticion.open("POST", `${API}files`);

    peticion.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgreso?.(Math.round((e.loaded * 100) / e.total));
    };

    peticion.onload = () => {
      if (peticion.status < 200 || peticion.status >= 300) {
        rechazar(new ErrorApi(peticion.status, "No pudimos subir tu archivo."));
        return;
      }
      try {
        const cuerpo = JSON.parse(peticion.responseText);
        resolver({
          sessionId: cuerpo?.data?.session_id ?? "",
          fileId: cuerpo?.data?.id ?? "",
        });
      } catch {
        rechazar(new ErrorApi(500, "El servidor respondió algo que no entendimos."));
      }
    };

    peticion.onerror = () => rechazar(new ErrorApi(0, "No hay conexión con el servidor."));
    peticion.send(datos);
  });

/**
 * Espera el evento de Firebase que dice que el CV ya fue analizado.
 *
 * Se filtra por `sessionId` Y por marca de tiempo: el canal `leads/` es comun a
 * todas las subidas, y sin los dos filtros llega el evento de otra persona --
 * que es exactamente el bug que el builder viejo documenta en sus comentarios.
 */
export const esperarAnalisis = (sessionId: string): Promise<number> =>
  new Promise((resolver, rechazar) => {
    const app = initializeApp(firebaseConfig, `subida-${Date.now()}`);
    const db = getDatabase(app);
    const desde = Date.now();

    const consulta = query(ref(db, "leads/"), orderByChild("timestamp"), startAt(desde));

    let cerrado = false;
    const cerrar = () => {
      cerrado = true;
      cancelar();
      clearTimeout(reloj);
    };

    const reloj = setTimeout(() => {
      if (cerrado) return;
      cerrar();
      rechazar(
        new ErrorApi(
          408,
          "Tu hoja de vida está tardando más de lo normal. Puedes empezar en blanco y pegar tu información.",
        ),
      );
    }, ESPERA_MAX_MS);

    const cancelar = onChildAdded(consulta, (snap) => {
      if (cerrado) return;
      const evento = snap.val();
      if (!evento?.timestamp || evento.timestamp < desde) return;
      if (sessionId && evento.sessionId !== sessionId) return;

      if (evento.error) {
        cerrar();
        rechazar(
          new ErrorApi(422, "No pudimos leer tu hoja de vida. Prueba con otro archivo."),
        );
        return;
      }

      if (evento.cvId) {
        cerrar();
        resolver(Number(evento.cvId));
      }
    });
  });

/** Crea una hoja de vida vacía, para quien empieza desde cero. */
export const crearVacia = async (): Promise<number> => {
  const res = await fetch(`${API}items/curriculum_vitae`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "published", terms: true }),
  });
  if (!res.ok) throw new ErrorApi(res.status, "No pudimos crear tu hoja de vida.");
  const cuerpo = await res.json();
  return Number(cuerpo?.data?.id);
};
